import { computed } from 'vue'
import { isFunction } from '@/global/utils/utils'
import { api } from '@/global/services/api'
import { registeredTableHandlers } from '@/global/components/view-layouts/tabs-layout/config/registered-table-handlers'
import { useTableService } from '@/global/composables/table/useTableService'
import { showDetailsAction, deleteAction } from '@/global/components/table/helpers/tableActions'
import { useActiveStatus } from '@/modules/road-maintenance-module/winter-service-v2/common/composables/useActiveStatus'
import {
  ACTIVE_STATUS,
  WINTER_SERVICE_MODULE
} from '@/modules/road-maintenance-module/winter-service-v2/common/config/constants'
import {
  IRREGULARITIES_ENDPOINT,
  IRREGULARITIES_TABLE_KEY,
  IRREGULARITIES_UI_VIEW,
  IRREGULARITY_DESCRIPTION_KEY,
  IRREGULARITY_IS_ACTIVE_FILTER_VALUES,
  IRREGULARITY_IS_ACTIVE_KEY
} from '@/modules/road-maintenance-module/winter-service-v2/settings/irregularities/config/constants'

const { buildConfig } = useTableService()

const API_CONFIG = {
  get: {
    module: WINTER_SERVICE_MODULE,
    route: IRREGULARITIES_ENDPOINT,
    // The index endpoint narrows the list down through `filters` only - it has no
    // free-text `search` param of its own - so what was typed into the search box
    // is folded into the description filter. An explicitly set filter wins.
    queryParams: ({ params }) => {
      const { search, filters } = params || {}
      const searchTerm = typeof search === 'string' ? search.trim() : ''

      if (!searchTerm || filters?.[IRREGULARITY_DESCRIPTION_KEY]) {
        return {}
      }

      return {
        filters: {
          ...(filters || {}),
          [IRREGULARITY_DESCRIPTION_KEY]: searchTerm
        }
      }
    }
  }
}

export function useTable (options) {
  const {
    trans,
    openIrregularityDialogFn,
    openInfoDialogFn,
    closeInfoDialogFn,
    setInfoDialogLoaderFn
  } = options || {}

  const { statusCellConfig, buildStatusCell, getStatusLabel } = useActiveStatus({ trans })

  const fetchTableData = () => {
    const { fetchData } = registeredTableHandlers?.[IRREGULARITIES_TABLE_KEY] || {}
    if (!isFunction(fetchData)) {
      return
    }
    fetchData()
  }

  const onCreateNewIrregularity = () => {
    openIrregularityDialogFn({
      title: trans('road-maintenance/winter_service2/settings/irregularities.new_irregularity'),
      editId: null,
      description: null,
      isActive: null
    })
  }

  const onShowIrregularityDetails = (event) => {
    const { id, description, is_active: isActive } = event?.data || {}
    if (!id) {
      return
    }

    openIrregularityDialogFn({
      title: trans('road-maintenance/winter_service2/settings/irregularities.edit_irregularity'),
      editId: id,
      description: description || null,
      isActive
    })
  }

  const deleteIrregularity = async (event) => {
    try {
      const { id } = event?.data || {}
      if (!id) {
        return
      }

      setInfoDialogLoaderFn(true)
      await api(WINTER_SERVICE_MODULE).delete(`${IRREGULARITIES_ENDPOINT}/${id}`)
      closeInfoDialogFn()
      fetchTableData()
    }
    catch (exception) {
      console.error(exception)
    }
    finally {
      setInfoDialogLoaderFn(false)
    }
  }

  const onIrregularityDelete = (event) => {
    openInfoDialogFn({
      title: trans('road-maintenance/winter_service2/settings/irregularities.delete_confirmation_title'),
      message: trans('road-maintenance/winter_service2/settings/irregularities.delete_confirmation_message'),
      onConfirm: () => deleteIrregularity(event)
    })
  }

  // The row carries a boolean `is_active`; the shared status indicator speaks in
  // active/inactive statuses, so it is translated here at the boundary.
  const cellsConfig = computed(() => ({
    [IRREGULARITY_IS_ACTIVE_KEY]: {
      ...statusCellConfig,
      custom_display_value: (data) => buildStatusCell(
        data?.[IRREGULARITY_IS_ACTIVE_KEY] ? ACTIVE_STATUS.active : ACTIVE_STATUS.inactive
      )
    }
  }))

  const headers = computed(() => ([
    {
      label: trans('road-maintenance/winter_service2/settings/irregularities.id'),
      key: 'id',
      always_visible: true
    },
    {
      label: trans('road-maintenance/winter_service2/settings/irregularities.name'),
      key: IRREGULARITY_DESCRIPTION_KEY,
      // The only listed column the endpoint can order by (next to the timestamps,
      // which this table does not show).
      sortable: true,
      always_visible: true
    },
    {
      label: trans('road-maintenance/winter_service2/settings/irregularities.status'),
      key: IRREGULARITY_IS_ACTIVE_KEY,
      always_visible: true
    }
  ]))

  const filtersConfig = computed(() => ({
    fields: [
      {
        key: IRREGULARITY_DESCRIPTION_KEY,
        label: trans('road-maintenance/winter_service2/settings/irregularities.name')
      },
      {
        key: IRREGULARITY_IS_ACTIVE_KEY,
        label: trans('road-maintenance/winter_service2/settings/irregularities.status'),
        type: 'autocomplete',
        items: [
          {
            id: IRREGULARITY_IS_ACTIVE_FILTER_VALUES.active,
            name: getStatusLabel(ACTIVE_STATUS.active)
          },
          {
            id: IRREGULARITY_IS_ACTIVE_FILTER_VALUES.inactive,
            name: getStatusLabel(ACTIVE_STATUS.inactive)
          }
        ]
      }
    ]
  }))

  const actionsConfig = computed(() => ([
    showDetailsAction(trans, onShowIrregularityDetails),
    deleteAction(trans, onIrregularityDelete)
  ]))

  const breadcrumbs = computed(() => ({
    items: [
      {
        text: trans('road-maintenance/winter_service2.settings')
      },
      {
        text: trans('road-maintenance/winter_service2/settings.irregularities'),
        bolded: true
      }
    ]
  }))

  const config = computed(() => ({
    tableConfig: buildConfig({
      key: IRREGULARITIES_TABLE_KEY,
      uiView: IRREGULARITIES_UI_VIEW,
      apiConfig: API_CONFIG,
      rowDblClick: onShowIrregularityDetails,
      search_and_buttons_config: {
        search_field_config: {
          placeholder: trans('base.tracker_search'),
          prependInnerIcon: 'mdi-magnify'
        },
        showNewBtn: true,
        newClick: onCreateNewIrregularity,
        newButtonConfig: {
          label: trans('road-maintenance/winter_service2/settings/irregularities.new_irregularity')
        }
      },
      // Refresh, filters, columns visibility and the table/grid switcher are all
      // on by default - buildConfig fills them in.
      tableConfig: {
        cellsConfig: cellsConfig.value,
        headers: headers.value,
        actionsConfig: actionsConfig.value,
        filtersConfig: filtersConfig.value
      }
    }),
    breadcrumbs: breadcrumbs.value
  }))

  return {
    config,
    fetchTableData
  }
}
